import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../lib/api'
import type {
  NoScheduleReportResponse,
  NoScheduleReportFilters,
  RevenueVsPayrollFilters,
  RevenueVsPayrollResponse,
  SalariesReportFilters,
  SalariesReportResponse,
  ScheduleEffectivenessFilters,
  ScheduleEffectivenessResponse,
  TimesheetReportResponse,
  TimesheetReportFilters,
  TalentReportResponse,
  TalentReportFilters,
  WaiterExcellenceResponse,
  WaiterExcellenceFilters,
} from '../types/reports.types'

const REPORTS_QUERY_KEY = 'reports'

export const useNoScheduleReport = (filters: NoScheduleReportFilters, enabled: boolean = true) => {
  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'no-schedule', filters],
    queryFn: async (): Promise<NoScheduleReportResponse> => {
      const response = await apiClient.getNoScheduleReport(filters)
      return response
    },
    enabled,
    staleTime: 60 * 1000, // 1 minute
    retry: 2,
  })
}

export const useTalentReport = (filters: TalentReportFilters, enabled: boolean = true) => {
  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'talent', filters],
    queryFn: async (): Promise<TalentReportResponse> => {
      const response = await apiClient.getTalentReport(filters)
      return response
    },
    enabled,
    staleTime: 60 * 1000, // 1 minute
    retry: 2,
  })
}

export const useTimesheetReport = (filters: TimesheetReportFilters, enabled: boolean = true) => {
  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'timesheet', filters],
    queryFn: async (): Promise<TimesheetReportResponse> => {
      const response = await apiClient.getTimesheetReport(filters)
      return response
    },
    enabled: enabled && !!filters.year && !!filters.month,
    staleTime: 60 * 1000, // 1 minute
    retry: 2,
  })
}

export const useScheduleEffectivenessReport = (
  filters: ScheduleEffectivenessFilters,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'schedule-effectiveness', filters],
    queryFn: async (): Promise<ScheduleEffectivenessResponse> => {
      const response = await apiClient.getScheduleEffectivenessReport(filters)
      return response
    },
    enabled: enabled && !!filters.from && !!filters.to,
    staleTime: 60 * 1000, // 1 minute
    retry: 2,
  })
}

export const useSalariesReport = (filters: SalariesReportFilters, enabled: boolean = true) => {
  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'salaries', filters],
    queryFn: async (): Promise<SalariesReportResponse> => {
      const response = await apiClient.getSalariesReport(filters)
      return response
    },
    enabled: enabled && !!filters.period,
    staleTime: 60 * 1000,
    retry: 2,
  })
}

export const useWaiterExcellenceReport = (
  filters: WaiterExcellenceFilters,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'waiter-excellence', filters],
    queryFn: async (): Promise<WaiterExcellenceResponse> => {
      const response = await apiClient.getWaiterExcellenceReport(filters)
      return response
    },
    enabled: enabled && !!filters.from && !!filters.to,
    staleTime: 60 * 1000,
    retry: 1,
  })
}

export const useRevenueVsPayrollReport = (
  filters: RevenueVsPayrollFilters,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [REPORTS_QUERY_KEY, 'revenue-vs-payroll', filters],
    queryFn: async (): Promise<RevenueVsPayrollResponse> => {
      const response = await apiClient.getRevenueVsPayrollReport({
        locationId: filters.locationId!,
        year: filters.year,
        month: filters.month,
        departmentIds:
          filters.departmentIds && filters.departmentIds.length > 0
            ? filters.departmentIds.join(',')
            : undefined,
        includeTaxes: filters.includeTaxes ? true : undefined,
      })
      return response
    },
    enabled: enabled && !!filters.locationId && !!filters.year && !!filters.month,
    staleTime: 60 * 1000,
    retry: 1,
  })
}
